<template>
    <div class="ViewMap-MAIN">
        <div ref="mapEl" class="map"></div>
    </div>
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader";

export default {
    name: "ViewMap",
    props: {
        searchTerm: { type: String, required: true },
        apiKey: { type: String, required: true }, // pásala como prop o desde .env
        radius: { type: Number, default: 5000 },  // metros
        zoom: { type: Number, default: 14 }
    },
    data() {
        return {
            map: null,
            userMarker: null,
            placeMarkers: [],
            infoWindow: null,
            lastCenter: null,
            dictionary: {
                "chilis": "chili's",
                "cinepolis": "cinépolis"
            }
        };
    },
    watch: {
        searchTerm(newVal) {
            if (this.lastCenter) {
                this.searchNearby(this.lastCenter, newVal);
            }
        }
    },
    methods: {
        normalizedTerm(term) {
            const raw = (term || "").trim().toLowerCase();
            return this.dictionary[raw] || raw;
        },

        async loadGoogle() {
            const loader = new Loader({
                apiKey: this.apiKey,
                libraries: ["places"],
                version: "quarterly"
            });
            return await loader.load();
        },

        async initMapAt(lat, lng) {
            const google = await this.loadGoogle();
            const center = new google.maps.LatLng(lat, lng);
            this.lastCenter = center;

            this.map = new google.maps.Map(this.$refs.mapEl, {
                center,
                zoom: this.zoom,

                mapTypeControl: false,
                streetViewControl: false,

                gestureHandling: 'none',
                zoomControl: false,
                keyboardShortcuts: false,

                fullscreenControl: false,
            });

            this.infoWindow = new google.maps.InfoWindow();

            // marcador de mi ubicación
            this.userMarker?.setMap(null);
            this.userMarker = new google.maps.Marker({
                position: center,
                map: this.map,
                title: "Mi ubicación",
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 10,
                    fillColor: "#4285F4", // azul Google
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: "#fff"
                }
            });

            this.userCircle?.setMap(null);
            this.userCircle = new google.maps.Circle({
                map: this.map,
                center,
                radius: this.radius,
                fillColor: "#4285F4",
                fillOpacity: 0.1,
                strokeColor: "#4285F4",
                strokeOpacity: 0.5,
                strokeWeight: 1
            });

            // primera búsqueda
            this.searchNearby(center, this.searchTerm);
        },

        clearPlaceMarkers() {
            this.placeMarkers.forEach(m => m.setMap(null));
            this.placeMarkers = [];
        },

        searchNearby(center, term) {
            if (!this.map) return;

            const google = window.google;
            const service = new google.maps.places.PlacesService(this.map);
            const keyword = this.normalizedTerm(term);

            this.clearPlaceMarkers();

            const request = {
                location: center,
                radius: this.radius,
                keyword: keyword || undefined,
                // opcional (ayuda un poco): solo lugares "relevantes"
                // rankBy: google.maps.places.RankBy.PROMINENCE, // default
            };

            service.nearbySearch(request, (results, status) => {
                if (status !== google.maps.places.PlacesServiceStatus.OK || !results) return;

                // filtro estricto por marca/nombre
                const filtered = results.filter(p => this.matchesBrand(p.name, keyword));

                const bounds = new google.maps.LatLngBounds();
                bounds.extend(this.userMarker.getPosition());

                filtered.forEach(place => {
                if (!place.geometry?.location) return;

                const marker = new google.maps.Marker({
                    map: this.map,
                    position: place.geometry.location,
                    title: place.name
                });

                marker.addListener("click", () => {
                    const address = place.vicinity || place.formatted_address || "";
                    const rating = place.rating ? `★ ${place.rating}` : "";

                    const distM = this.distanceMeters(
                    center.lat(), center.lng(),
                    place.geometry.location.lat(), place.geometry.location.lng()
                    );

                    this.infoWindow.setContent(
                    `<div style="min-width:220px">
                        <strong>${place.name}</strong><br/>
                        ${address}<br/>
                        ${rating}<br/>
                        ${Math.round(distM)} m
                    </div>`
                    );
                    this.infoWindow.open(this.map, marker);
                });

                this.placeMarkers.push(marker);
                bounds.extend(place.geometry.location);
                });

                if (this.placeMarkers.length) {
                this.map.fitBounds(bounds, 48);
                }
            });
        },

        normalizeForCompare(str) {
            return (str || "")
                .toLowerCase()
                .trim()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "") // acentos
                .replace(/[^a-z0-9\s]/g, " ")    // signos
                .replace(/\s+/g, " ")
                .trim();
        },

        matchesBrand(placeName, searchTerm) {
            const name = this.normalizeForCompare(placeName);
            const term = this.normalizeForCompare(searchTerm);

            if (!name || !term) return false;

            const stop = new Set(["la", "el", "los", "las", "de", "del", "y", "the", "a", "an", "hotel"]);
            const tokens = term.split(" ").filter(t => t && !stop.has(t));

            // si tras quitar stopwords no queda nada, no matchees nada
            if (!tokens.length) return false;

            // exige que el término (sin stopwords) aparezca junto
            const termCompact = tokens.join(" ");

            // regla: que el nombre contenga el término compacto
            // "la provoleta" -> "provoleta"
            return name.includes(termCompact);
        },

        distanceMeters(lat1, lng1, lat2, lng2) {
            const R = 6371000;
            const toRad = (x) => (x * Math.PI) / 180;

            const dLat = toRad(lat2 - lat1);
            const dLng = toRad(lng2 - lng1);

            const a =
                Math.sin(dLat / 2) ** 2 +
                Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

            return 2 * R * Math.asin(Math.sqrt(a));
        },

        getLocation() {
            if (!("geolocation" in navigator)) {
                this.initMapAt(19.432608, -99.133209); // fallback CDMX
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (pos) => this.initMapAt(pos.coords.latitude, pos.coords.longitude),
                () => this.initMapAt(19.432608, -99.133209),
                { enableHighAccuracy: true, timeout: 10000 }
            );
        }
    },
    mounted() {
        this.getLocation();
    }
};
</script>

<style scoped>
.ViewMap-MAIN {
    width: 100%;
}

.map {
    width: 100%;
    height: 450px;
    border: 0;
}
</style>